import EmbeddedTypeGroupBoxFactory from '@/Factories/ControlFactories/EmbeddedTypeGroupBoxFactory';
import ComponentManager from '@/Management/ComponentManager';
import RADApplication from '@/Management/RADApplication';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import * as CoreFrameDTOs from "coreframetypings/dto";
import ChangelogGrid from '@/Components/ChangelogGrid/ChangelogGrid';
import { IGridControlFactory } from '#/DefaultViewExtensionInterfaces';
import ChangelogAssociationGridFactory from '@/Components/ChangelogGrid/ChangelogAssociationGridFactory';

export default class ChangelogGridFactory extends EmbeddedTypeGroupBoxFactory implements IGridControlFactory {

    private readonly showComments: boolean;
    private readonly commentRequired: boolean;

    /**
     * @override
     */
    public control: ChangelogGrid;

    private associationGridFactory: KnockoutObservable<ChangelogAssociationGridFactory>;
    private containerPostfix: KnockoutComputed<string>;
    
    constructor(params: any) {
        super(params);

        this.showComments = this.paramOrDefault(params.showComments, true);
        this.commentRequired = this.paramOrDefault(params.commentRequired, false);

        this.createChildren();

        this.associationGridFactory = this.observableOrDefault(null);
        this.containerPostfix = this.createContainerPostfixComputed();
    }

    /**
     * @override
     * @inheritdoc 
     */
    public createControl(): ChangelogGrid {
        const grid = new ChangelogGrid(this.viewModel);

        // Das Association-Grid sollte (asynchron) erzeugt worden sein, wenn das Widget als erstellt gilt
        this.subscribeUntil(grid.widget, () => {
            if (this.isDisposed)
                return true;

            const factory = this.getAssociationGridFactory();
            this.associationGridFactory(factory);
            return factory != null;
        });

        return grid;
    }

    private getAssociationGridFactory(): ChangelogAssociationGridFactory {
        const childNames = this.detailViewItem.children.map(c => c.name);

        for (const factory of this.context.getCreatedControlFactories()) {
            if (childNames.includes(factory.detailViewItem.name) && factory instanceof ChangelogAssociationGridFactory)
                return factory;
        }

        return null;
    }

    /**
     * Gibt an, ob die Eingabe eines Kommentars erforderlich ist
     */
    private isCommentRequired(): boolean {
        return this.commentRequired && (this.context as DetailViewContext).action !== 'CREATE';
    }

    /**
     * Pr�ft, ob die Changelogs angezeigt werden sollen
     */
    private isChangelogActive(): boolean {
        const showCommentsInInstance = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowCommentsInInstance');
        return showCommentsInInstance && this.showComments && !(this.context as DetailViewContext).genericObject.isHistorized;
    }

    /**
     * Erstellt DVG-Items f�r die einzelnen Eigenschaften (Neuer Kommentar / Assoziation)
     */
    private createChildren() {
        const dvgItemCopy = JSON.parse(JSON.stringify(this.detailViewItem)) as CoreFrameDTOs.IDVGItemDTO;
        dvgItemCopy.uiGenerator = dvgItemCopy.uiGenerator.replace('ChangelogGrid', 'ChangelogAssociationGrid');
        dvgItemCopy.uiGenerator = this.addParameterToExistingUiGenerator(dvgItemCopy.uiGenerator, `IsChangeLogActive=${this.isChangelogActive()}`);
        
        this.detailViewItem.children = [dvgItemCopy];

        if (this.isSummaryViewMode)
            return;

        const dvgItemForTextBox: Partial<CoreFrameDTOs.IDVGItemDTO> = {
            uiGenerator: 'ChangelogMLStringPopupBox?Multiline=true',
            ergName: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.PERSISTENTOBJECT_CHANGELOGS_NEWCOMMENT',
            tooltip: 'RAD.RADFOUNDATION.PROBLEMDOMAIN.PERSISTENTOBJECT_CHANGELOGS_NEWCOMMENT_TOOLTIP',
            required: this.isCommentRequired(),
            children: [],
            length: 1024,
            dotNetTypeName: '',
            name: this.detailViewItem.name + '.new',
        };

        this.detailViewItem.children.splice(0, 0, dvgItemForTextBox as any);
    }

    /**
     * F�gt einen Parameter zu einem bestehenden UIGenerator hinzu
     * @param uiGenerator
     * @param additionalParameters
     */
    private addParameterToExistingUiGenerator(uiGenerator: string, additionalParameters: string) {
        if (uiGenerator.indexOf('?') > -1)
            additionalParameters = `&${additionalParameters}`;
        else
            additionalParameters = `?${additionalParameters}`;

        return uiGenerator + additionalParameters;
    }

    private createContainerPostfixComputed(): KnockoutComputed<string> {
        return this.defineComputed(() => {
            if (this.isDisposed)
                return '';

            const factory = this.associationGridFactory();
            if (factory == null)
                return '';

            const postfix = factory.getContainerPostfix();
            return postfix();
        });
    }

    public getContainerPostfix(): KnockoutObservable<string> {
        if (this.isDisposed)
            return this.observableOrDefault('');

        return this.containerPostfix;
    }
}

ComponentManager.setFactory('ChangelogGrid', ChangelogGridFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');